import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { IMqttMessage } from "ngx-mqtt";
import { Observable, Subscription } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { PlantCode } from "src/model/class/general classes/plant-code";
import { PlantCodeABB } from "src/model/class/general classes/plant-code-abb";
import { PlantCodeGeneric } from "src/model/class/general classes/plant-code-generic";
import { Settings } from "src/model/class/settings";
import { GatewayKind } from "src/components/shared-components/enum/enumGateway";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ApplicationTheme, ManufacturerSystem } from "src/components/shared-components/enum/enumMisc";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import {
  plantCodeAddressValueChangeAction,
  plantCodesLoadInitStartAction,
} from "src/state/actions/plant-code.action";
import {
  currentUserData,
  isUserAuthenticated,
} from "src/state/selectors/authentication.selectors";
import {
  ABBPlantCodes,
  genericPlantCodes,
} from "src/state/selectors/plant-code.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { AuthenticationService } from "./authentication.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { ThemeService } from "./theme.service";

@Injectable({
  providedIn: "root",
})
export class PlantCodeService implements iEvent {
  private _selectCurrentUserSubscription: Subscription = null;
  private _serverSettingSubscription: Subscription = null;
  private _genericPlantCodesSubscription: Subscription = null;
  private _abbPlantCodesSubscription: Subscription = null;
  private _logContext: string = "Plant-code.service";
  private _serverSetting: Settings = null;

  private _listPlantCodeGeneric: Array<PlantCodeGeneric> = null;
  private _listABBPlantCodes: Array<PlantCodeABB> = null;

  public _eventSubscriptions: Array<Subscription> = null;
  public _mqttAddressSubscriptionsMap: any = null;

  public needToInitializa: boolean = true;

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _eventService: EventService,
    private _authService: AuthenticationService,
    private _loggerService: LoggerService,
    private _mqttClientService: MqttClientService,
    private _themeService: ThemeService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.actIfLoggedIn();
    this.subscribeEvents();
    this.loadServerSettingsFromStore();
    this.loadPlantsCodeFromStore();
  }

  ngOnDestroy() {
    this._serverSettingSubscription?.unsubscribe();

    this._genericPlantCodesSubscription?.unsubscribe();
    this._abbPlantCodesSubscription?.unsubscribe();
    this._selectCurrentUserSubscription?.unsubscribe();

    this.unsubscribeEvents();
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.Init();
  }

  /**
   * Get plantCodeStruct data from API. If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */

  public getPlantsCodes(): Observable<PlantCode> {
    return this._http.get<PlantCode>(
      environment.httpServerRoot + AppSettings.API_URL_PLANTCODE
    );
  }

  /**
   * Return plant code by manufacturer system
   *
   * @param manufacturer Manufactuer system to get plant code
   * @param MACPlantCode mac plant code (USED ONLY FOR "ABB_MILLENIUM" manifacturer system, IF we are in "STAND_ALONE" mode)
   * @param idMACPlantCode ID for mac plant code (USED ONLY FOR "ABB_MILLENIUM" manifacturer system, IF we are in "STANDARD" mode)
   * @returns
   */
  public calculatePlantCode(
    manufacturer: ManufacturerSystem,
    MACPlantCode: number = 0,
    idMACPlantCode: number = -1
  ): number {
    let returnedPlantCode: number;
    let selectedPlantCode: PlantCodeGeneric = null;

    this._loggerService.log(LogLevel.LOG_INFO, this._logContext, this._logContext, "calculatePlantCode()",
      "Passed MACPlantCode " + MACPlantCode + " | passed idMACPlantCode: " + idMACPlantCode
    );

    switch (manufacturer) {
      case ManufacturerSystem.VIMAR_WELL_CONTACT_PLUS:
        selectedPlantCode = this._listPlantCodeGeneric.find(
          (plant: PlantCodeGeneric) => plant.kind == GatewayKind.KNX
        );

        if (selectedPlantCode) {
          returnedPlantCode = parseInt(selectedPlantCode.plantCode);
        }
        break;
      case ManufacturerSystem.BIG_VIRTUAL_MIFARE:
        selectedPlantCode = this._listPlantCodeGeneric.find(
          (plant: PlantCodeGeneric) => plant.kind == GatewayKind.ACCESS_CONTROL
        );

        if (selectedPlantCode) {
          returnedPlantCode = parseInt(selectedPlantCode.plantCode);
        }
        break;
      case ManufacturerSystem.ABB_MILLENIUM:
        //returnedPlantCode = this.getMACByIDPlantCode(idMacPlantCode);
        if (idMACPlantCode != -1) {
          returnedPlantCode = this.getMACByIDPlantCode(idMACPlantCode);
        } else {
          returnedPlantCode = this.checkMACPlantCode(MACPlantCode);
        }

        break;
    }

    if (Number.isNaN(returnedPlantCode)) {
      returnedPlantCode = 0;
    }

    return returnedPlantCode;
  }


  /**
   * Get plant code by ABB MAC Group
   *
   * @param idMacPlantCode  ID Mac Plant code
   * @returns
   */
  public getMACByIDPlantCode(idMacPlantCode: number): number {
    let plantCode: number = 0;

    try {
      if (this._listABBPlantCodes.length > 0) {
        plantCode =
          (
            this._listABBPlantCodes.find(
              (abbPlantCode: PlantCodeABB) => abbPlantCode.id === idMacPlantCode
            ) || null
          )?.code || 0;
      }
    } catch (exception) {
      throw new Error();
    }

    return plantCode;
  }


  /**
   * This method checks if the passed MACPlantCode is present inside the ABBMillenium plant code list assigned to the current device. 
   * (and previously fetched from server). If so, it returns the MACPlantCode itself; otherwise, it returns 0.
   * @param MACPlantCode The MACPlantCode assigned to a ABBMillenium card.
   * @returns 
   */
  public checkMACPlantCode(MACPlantCode: number): number {
    let plantCode: number = 0;

    if (this.isMACPlantCodePresentOnDevice(MACPlantCode)) {
      plantCode = MACPlantCode;
    } else {
      plantCode = 0;
    }//

    return plantCode;
  }
  //

  private isMACPlantCodePresentOnDevice(MACPlantCode: number): boolean {
    let MACPlantCodeIsPresent: boolean = false;

    for (let i = 0; i < this._listABBPlantCodes.length && !MACPlantCodeIsPresent; i++) {
      MACPlantCodeIsPresent = this._listABBPlantCodes[i].code == MACPlantCode;
    }

    return MACPlantCodeIsPresent;
  }

  /**
   * Subscribe to plant code list addresses change with mqtt client service
   */
  public subscribePlantCodeAddresses(): void {
    this._listPlantCodeGeneric.map(
      async (plantCodeGeneric: PlantCodeGeneric) => {
        const fullAddress: string =
          plantCodeGeneric.instanceGUID +
          "_" +
          plantCodeGeneric.plantCodeAddress;

        const promsieObservable = await this.createObservableMqttMessage(
          fullAddress
        );

        const subscription: Subscription = promsieObservable
          .pipe(
            map((mqttMessageObject: any) => mqttMessageObject.Message),
            map((mqttMessage: any) => mqttMessage.Value)
          )
          .subscribe((mqttMessageValue: string) =>
            this.handleNewGenericPlantCodeTelegram(
              plantCodeGeneric.instanceGUID,
              plantCodeGeneric.plantCodeAddress,
              mqttMessageValue
            )
          );

        await this.checkLocalAddressSubscriptionMap(fullAddress, subscription);
      }
    );
  }

  //#region  MQTT Addresses
  public async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }

    return result;
  }

  /**
   * Unsubscribe to mqtt address
   */
  public async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }
  //#endregion

  /**
   * Load current user from store
   */
  private actIfLoggedIn(): void {
    this._selectCurrentUserSubscription = this._store
      .select(currentUserData)
      .pipe(
        distinctUntilChanged(
          (
            previousSession: GenericUserData,
            currentSession: GenericUserData
          ) => {
            let accessTokenPreviosSession: string =
              previousSession?.currentUser?.accessToken || "";
            let accessTokenCurrentSession: string =
              currentSession?.currentUser?.accessToken || "";

            return accessTokenPreviosSession === accessTokenCurrentSession;
          }
        ),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser),
        tap((currentUser: CurrentSessionData) => {
          if (
            currentUser &&
            this._authService.isAccessTokenValid(currentUser) &&
            this._authService.isRefreshTokenValid(currentUser)
          ) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "actIfLoggedIn()",
              "Logged in, initializing plant-code store"
            );

            this.Init();
          }
        })
      )
      .subscribe((currentUser: CurrentSessionData) => { });
  }

  /**
   * Create promise observable subscription for full plant code address
   * (INSTANCEGUID_ADDRESS)
   *
   * @param plantCodeFullAddress Address to subscribe (plant code full address)
   * @returns
   */
  private async createObservableMqttMessage(
    plantCodeFullAddress: string
  ): Promise<Observable<any>> {
    const promiseSubscriptionGuestAddress: Observable<IMqttMessage> =
      await this._mqttClientService.subscribeTopic(plantCodeFullAddress);

    return promiseSubscriptionGuestAddress.pipe(
      map((message: IMqttMessage) => JSON.parse(message.payload.toString()))
    );
  }

  /**
   * Handle new plant code receive value.
   * It dispatch action to update store value by reducer
   *
   * @param instanceGUID Instance GUID update plant code
   * @param address Address update plant code
   * @param plantCodeValue Plant code value
   */
  private handleNewGenericPlantCodeTelegram(
    instanceGUID: string,
    address: string,
    plantCodeValue: string
  ): void {
    this._store.dispatch(
      plantCodeAddressValueChangeAction({
        instanceGUID,
        address,
        plantCodeValue,
      })
    );
  }

  /**
   * Load server settings
   */
  private loadServerSettingsFromStore(): void {
    this._serverSettingSubscription = this._store
      .select(serverSettings)
      .subscribe((settings: Settings) => {
        this._serverSetting = settings;
      });
  }

  /**
   * Load plants code
   */
  private loadPlantsCodeFromStore() {
    this._abbPlantCodesSubscription = this._store
      .select(ABBPlantCodes)
      .subscribe((ABBPlantCodesList: Array<PlantCodeABB>) => {
        this._listABBPlantCodes = ABBPlantCodesList;
      });

    this._genericPlantCodesSubscription = this._store
      .select(genericPlantCodes)
      .subscribe((listGenericPlantCode: Array<PlantCodeGeneric>) => {
        this._listPlantCodeGeneric = listGenericPlantCode;
      });
  }

  /** Subscribe to EventService observables */
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.reloadServiceData();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        await this.unsubscribeToMQTTAddresses();

        this.needToInitializa = true;
      })
    );
  }

  /** Unsubscribe from all EventService observables */
  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }

  //#region "ONLY-PLANT-CODE-FROM-API" IMPLEMENTATION

  /**
   *  Start procedure to populate (from API) the local Store:
   *  it should be call in the costructor of all component/page that you need.
   */
  private Init(): void {
    // if (!this._themeService.isDigitalPlace) {
    //   this._store.dispatch(plantCodesLoadInitStartAction());
    // }

    if (this.needToInitializa) {
      this.needToInitializa = false;
    }
  }

  //#endregion
}
